// 首页 Hero 多栏布局行
import { parseHeroPercent } from '@/components/home/utils/heroTextBlocks'
import {
  applyRowTextMeta,
  splitTextsToRowsWithMeta
} from '@/components/home/utils/heroRowFitMeta'
import {
  migrateColumnBackgroundFromRows,
  resolveColumnBackgroundStyle,
  syncColumnBackgroundFields
} from '@/components/home/utils/heroRowBackground'

export { resolveColumnBackgroundStyle }

export const HERO_MEDIA_FIT = {
  COVER: 'COVER',
  CONTAIN: 'CONTAIN'
}

/** 标准化媒体适配模式为 COVER 或 CONTAIN */
export function normalizeMediaFit(value, fallback = HERO_MEDIA_FIT.COVER) {
  return String(value || fallback).toUpperCase() === HERO_MEDIA_FIT.CONTAIN
    ? HERO_MEDIA_FIT.CONTAIN
    : HERO_MEDIA_FIT.COVER
}

/** 根据 mediaFit 返回 CSS background-size 值 */
export function resolveColumnRowBackgroundSize(row) {
  return normalizeMediaFit(row?.mediaFit) === HERO_MEDIA_FIT.CONTAIN ? 'contain' : 'cover'
}

/** 构建 COLUMN 行背景图的 CSS 内联样式 */
export function buildColumnRowMediaStyle(url, row) {
  if (!url) return {}
  return {
    backgroundImage: `url("${url}")`,
    backgroundSize: resolveColumnRowBackgroundSize(row),
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  }
}

/** 获取行级背景图 URL（优先 backgroundUrl；窄屏可优先手机图） */
function rowImageUrl(row, { mobile = false } = {}) {
  if (mobile) {
    return row?.mobileBackgroundUrl || row?.mobileMediaUrl || ''
  }
  return row?.backgroundUrl || row?.mediaUrl || ''
}

/** 将文字按行数拆分并深拷贝 */
function splitTextsToRows(texts, rowCount) {
  return splitTextsToRowsWithMeta(texts, rowCount).map((rowTexts) =>
    (rowTexts || []).map((entry) => ({ ...entry }))
  )
}

/** 从旧版单列/双图字段结构迁移为 rows 数组 */
function rowsFromLegacyItem(item, rowCount) {
  if (rowCount === 1) {
    const url = item.backgroundUrl || item.mediaUrl || ''
    return applyRowTextMeta(
      [
        {
          backgroundUrl: url,
          mediaUrl: url,
          mobileBackgroundUrl: item.mobileBackgroundUrl || item.mobileMediaUrl || '',
          mobileMediaUrl: item.mobileMediaUrl || item.mobileBackgroundUrl || '',
          texts: item.texts || []
        }
      ],
      rowCount
    )
  }

  const topUrl = item.backgroundUrl || ''
  const bottomUrl = item.mediaUrl || ''
  const [topTexts, bottomTexts] = splitTextsToRowsWithMeta(item.texts || [], rowCount)

  return applyRowTextMeta(
    [
      {
        backgroundUrl: topUrl,
        mediaUrl: topUrl,
        mobileBackgroundUrl: item.mobileBackgroundUrl || item.mobileMediaUrl || '',
        mobileMediaUrl: item.mobileBackgroundUrl || item.mobileMediaUrl || '',
        texts: topTexts.map((entry) => ({
          ...entry,
          posY: `${Math.round(columnPosYToRow(parseHeroPercent(entry.posY), 0, rowCount) * 10) / 10}%`
        }))
      },
      {
        backgroundUrl: bottomUrl,
        mediaUrl: bottomUrl,
        mobileBackgroundUrl: item.mobileMediaUrl || item.mobileBackgroundUrl || '',
        mobileMediaUrl: item.mobileMediaUrl || item.mobileBackgroundUrl || '',
        texts: bottomTexts.map((entry) => ({
          ...entry,
          posY: `${Math.round(columnPosYToRow(parseHeroPercent(entry.posY), 1, rowCount) * 10) / 10}%`
        }))
      }
    ],
    rowCount
  )
}

/** 标准化单行数据（背景图、mediaFit、文字） */
function normalizeRow(row, fallbackFit = HERO_MEDIA_FIT.COVER) {
  return {
    backgroundUrl: row.backgroundUrl || row.mediaUrl || '',
    mediaUrl: row.mediaUrl || row.backgroundUrl || '',
    mobileBackgroundUrl: row.mobileBackgroundUrl || row.mobileMediaUrl || '',
    mobileMediaUrl: row.mobileMediaUrl || row.mobileBackgroundUrl || '',
    mediaFit: normalizeMediaFit(row.mediaFit, fallbackFit),
    texts: row.texts || []
  }
}

/** 标准化 COLUMN 类型 hero 条目（含旧数据迁移与行补齐） */
export function normalizeColumnItem(item) {
  const rowCount = item.rowCount === 2 ? 2 : 1
  let rows

  if (Array.isArray(item.rows) && item.rows.length) {
    rows = item.rows.map((row, rowIndex) =>
      normalizeRow(row, normalizeMediaFit(item.rowMediaFits?.[rowIndex]))
    )
  } else {
    rows = rowsFromLegacyItem(item, rowCount)
  }

  while (rows.length < rowCount) {
    rows.push({
      backgroundUrl: '',
      mediaUrl: '',
      mediaFit: HERO_MEDIA_FIT.COVER,
      texts: []
    })
  }
  if (rows.length > rowCount) {
    rows.splice(rowCount)
  }

  rows = applyRowTextMeta(rows, rowCount)

  const normalized = {
    ...item,
    itemType: 'COLUMN',
    rowCount,
    rows
  }
  migrateColumnBackgroundFromRows(normalized)
  if (!normalized.rows[0].mobileBackgroundUrl && !normalized.rows[0].mobileMediaUrl) {
    const topMobile = item.mobileBackgroundUrl || item.mobileMediaUrl || ''
    if (topMobile) {
      normalized.rows[0].mobileBackgroundUrl = topMobile
      normalized.rows[0].mobileMediaUrl = topMobile
    }
  }
  if (normalized.rowCount === 2 && !normalized.rows[1].mobileBackgroundUrl && !normalized.rows[1].mobileMediaUrl) {
    const bottomMobile = item.mobileMediaUrl || item.mobileBackgroundUrl || ''
    if (bottomMobile) {
      normalized.rows[1].mobileBackgroundUrl = bottomMobile
      normalized.rows[1].mobileMediaUrl = bottomMobile
    }
  }
  syncColumnBackgroundFields(normalized)
  return normalized
}

/** 确保 COLUMN 条目的 rows 数组长度与 rowCount 一致 */
export function columnRows(column) {
  if (!column) return []
  const rowCount = column.rowCount === 2 ? 2 : 1
  if (!Array.isArray(column.rows)) {
    column.rows = []
  }
  while (column.rows.length < rowCount) {
    column.rows.push({
      backgroundUrl: '',
      mediaUrl: '',
      mediaFit: HERO_MEDIA_FIT.COVER,
      texts: []
    })
  }
  if (column.rows.length > rowCount) {
    column.rows.splice(rowCount)
  }
  return column.rows
}

/** 获取 COLUMN 行的背景图 URL */
export function columnRowImageUrl(row, options = {}) {
  return rowImageUrl(row, options)
}

/** 将全局 posY 转换为指定行内的局部纵向百分比 */
function columnPosYToRow(posY, rowIndex, rowCount) {
  const global = parseHeroPercent(posY)
  if (rowCount <= 1) return global
  const local = global * rowCount - rowIndex * 100
  return Math.min(100, Math.max(0, local))
}

export { columnPosYToRow }
